/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import type { Dispatch, SetStateAction } from 'react';

/* Import types */
import type { LocalizedMessage } from '@/types/i18n';
import type { SessionOrigin } from '@/types/ui';
import { clearEeCheckApiCache } from '@/api/support/eeCheckApiCache';

export const SESSION_STORAGE_KEY = 'energie-watchdog-api.access-token';
export const SESSION_ORIGIN_STORAGE_KEY = 'energie-watchdog-api.access-token.origin';
export const SESSION_IMPORTED_AT_STORAGE_KEY = 'energie-watchdog-api.access-token.imported-at';
export const SESSION_USERNAME_STORAGE_KEY = 'energie-watchdog-api.access-token.username';
export const LOGIN_REDIRECT_STORAGE_KEY = 'energie-watchdog-api.login-redirect';

interface SessionStateSetters {
    setAccessToken: Dispatch<SetStateAction<string>>;
    setSessionImportedAt: Dispatch<SetStateAction<string>>;
    setSessionOrigin: Dispatch<SetStateAction<SessionOrigin | null>>;
    setSessionUsername: Dispatch<SetStateAction<string>>;
}

interface SessionStatusSetter {
    setSessionStatus: Dispatch<SetStateAction<LocalizedMessage>>;
}

export interface StoredSessionState {
    accessToken: string;
    importedAt: string;
    origin: SessionOrigin | null;
}

export function persistSessionState(
    nextAccessToken: string,
    origin: SessionOrigin,
    setters: SessionStateSetters,
    importedAt: string = new Date().toISOString(),
    username: string = '',
) {
    const previousAccessToken = window.sessionStorage.getItem(SESSION_STORAGE_KEY);

    if (previousAccessToken && previousAccessToken !== nextAccessToken) {
        clearEeCheckApiCache();
    }

    window.sessionStorage.setItem(SESSION_STORAGE_KEY, nextAccessToken);
    window.sessionStorage.setItem(SESSION_ORIGIN_STORAGE_KEY, origin);
    window.sessionStorage.setItem(SESSION_IMPORTED_AT_STORAGE_KEY, importedAt);
    window.sessionStorage.setItem(SESSION_USERNAME_STORAGE_KEY, username);
    setters.setAccessToken(nextAccessToken);
    setters.setSessionOrigin(origin);
    setters.setSessionImportedAt(importedAt);
    setters.setSessionUsername(username);
}

export function readStoredSessionUsername(): string {
    if (typeof window === 'undefined') {
        return '';
    }

    if (!window.sessionStorage.getItem(SESSION_STORAGE_KEY)) {
        return '';
    }

    return window.sessionStorage.getItem(SESSION_USERNAME_STORAGE_KEY) ?? '';
}

export function readStoredSessionState(): StoredSessionState | null {
    const accessToken = window.sessionStorage.getItem(SESSION_STORAGE_KEY);

    if (!accessToken) {
        return null;
    }

    return {
        accessToken,
        importedAt: window.sessionStorage.getItem(SESSION_IMPORTED_AT_STORAGE_KEY) ?? '',
        origin: window.sessionStorage.getItem(
            SESSION_ORIGIN_STORAGE_KEY,
        ) as SessionOrigin | null,
    };
}

export function storeLoginRedirect(path: string) {
    if (typeof window === 'undefined' || !path.startsWith('/') || path === '/login') {
        return;
    }

    window.sessionStorage.setItem(LOGIN_REDIRECT_STORAGE_KEY, path);
}

export function consumeLoginRedirect(fallbackPath: string): string {
    if (typeof window === 'undefined') {
        return fallbackPath;
    }

    const storedPath = window.sessionStorage.getItem(LOGIN_REDIRECT_STORAGE_KEY);

    window.sessionStorage.removeItem(LOGIN_REDIRECT_STORAGE_KEY);

    return storedPath?.startsWith('/') && storedPath !== '/login'
        ? storedPath
        : fallbackPath;
}

export function clearPersistedSessionState(
    message: LocalizedMessage,
    setters: SessionStateSetters & SessionStatusSetter,
) {
    clearEeCheckApiCache();
    window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
    window.sessionStorage.removeItem(SESSION_ORIGIN_STORAGE_KEY);
    window.sessionStorage.removeItem(SESSION_IMPORTED_AT_STORAGE_KEY);
    window.sessionStorage.removeItem(SESSION_USERNAME_STORAGE_KEY);
    setters.setAccessToken('');
    setters.setSessionOrigin(null);
    setters.setSessionImportedAt('');
    setters.setSessionUsername('');
    setters.setSessionStatus(message);
}
